import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from 'lib/utils';
import logo from '../../../assets/images/selise-dice-logo.svg';
import { pickKaomojiForWorkingStatusLabel } from '../utils/kaomoji';
import {
  ROTATING_WORKING_STATUS_MESSAGES,
  WORKING_STATUS_FADE_MS,
} from '../utils/working-status-messages';
import { WorkingKaomoji } from './WorkingKaomoji';

export const SeliseAssistantLogo = ({ className }: { className?: string }) => (
  <img
    className={className ?? 'size-8 shrink-0 aspect-square object-cover object-left'}
    src={logo}
    alt="SELISE AI logo"
  />
);

interface MessageWorkingIndicatorProps {
  /** When set, show this label instead of rotating "Working" copy. */
  label?: string;
}

/** Rotating assistant status — label and kaomoji change together until stream UI ends. */
export const MessageWorkingIndicator = ({
  label: fixedLabel,
}: Readonly<MessageWorkingIndicatorProps> = {}) => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const fadeTimeoutRef = useRef<number>();
  const rotateTimeoutRef = useRef<number>();
  const label = fixedLabel || ROTATING_WORKING_STATUS_MESSAGES[index].label;
  const kaomoji = useMemo(() => pickKaomojiForWorkingStatusLabel(label), [label]);
  const fadeClassName = cn(
    'transition-opacity ease-in-out',
    visible ? 'opacity-100' : 'opacity-0',
  );
  const fadeStyle = { transitionDuration: `${WORKING_STATUS_FADE_MS}ms` };

  useEffect(() => {
    if (fixedLabel) {
      setVisible(true);
      return;
    }

    const scheduleNext = (currentIndex: number) => {
      const { durationMs } = ROTATING_WORKING_STATUS_MESSAGES[currentIndex];
      rotateTimeoutRef.current = window.setTimeout(() => {
        setVisible(false);
        fadeTimeoutRef.current = window.setTimeout(() => {
          const nextIndex = (currentIndex + 1) % ROTATING_WORKING_STATUS_MESSAGES.length;
          setIndex(nextIndex);
          setVisible(true);
          scheduleNext(nextIndex);
        }, WORKING_STATUS_FADE_MS);
      }, durationMs);
    };

    scheduleNext(0);

    return () => {
      if (rotateTimeoutRef.current != null) {
        window.clearTimeout(rotateTimeoutRef.current);
      }
      if (fadeTimeoutRef.current != null) {
        window.clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, [fixedLabel]);

  return (
    <div
      className="flex min-w-0 items-center gap-2 self-start"
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <p className={cn('working-label w-fit', fadeClassName)} style={fadeStyle}>
        {label}
      </p>
      <WorkingKaomoji kaomoji={kaomoji} className={fadeClassName} style={fadeStyle} />
    </div>
  );
};

/** Three-dot wave shown beside the assistant logo while waiting for a response. */
// export const MessageLoadingDots = () => (
//   <>
//     <style>{loadingWaveKeyframes}</style>
//     <div className="flex h-6 items-center gap-1.5 px-1 py-2" aria-hidden>
//       {[0, 1, 2].map((index) => (
//         <span
//           key={index}
//           className="inline-block h-2 w-2 rounded-full bg-primary-600"
//           style={{
//             animation: 'message-loading-wave 1.6s ease-in-out infinite',
//             animationDelay: `${index * 0.18}s`,
//           }}
//         />
//       ))}
//     </div>
//   </>
// );

export const MessageLoadingDots = MessageWorkingIndicator;

export const MessageLoading = () => {
  return (
    <div
      className="flex items-center justify-start gap-4"
      role="status"
      aria-live="polite"
      aria-label="Assistant is thinking"
    >
      <SeliseAssistantLogo />
      <MessageWorkingIndicator />
    </div>
  );
};
